import { Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { generatePath, useParams } from "react-router";

import { GqlPlannerVisitStatus } from "~/__generated__";
import { MapPanel } from "~/modules/map/components/MapPanel";

import { ProjectReviewVisitV2ApprovalButton } from "../ProjectReviewVisitV2ApprovalButton";
import { ProjectReviewVisitV2ApprovalDisplay } from "../ProjectReviewVisitV2ApprovalDisplay";
import { ProjectReviewVisitV2OverviewPanelFragment } from "./fragments.generated";
import { ProjectReviewVisitV2OverviewNavigation } from "./ProjectReviewVisitV2OverviewNavigation";
import { ProjectReviewVisitV2StatusHandler } from "./ProjectReviewVisitV2StatusHandler";

type Props = {
    visit: ProjectReviewVisitV2OverviewPanelFragment;
    onApprove?: () => Promise<void>;
    loading?: boolean;
    panelStatus: Record<string, boolean>;
    isEverythingReady: boolean;
    showFinalReportButton: boolean;
};

export function ProjectReviewVisitV2OverviewPanel({
    visit,
    onApprove,
    loading = false,
    panelStatus,
    isEverythingReady,
    showFinalReportButton,
}: Props) {
    const { clientSlug, projectSlug, visitId } = useParams();
    const { t } = useTranslation("project");

    if (!clientSlug || !projectSlug || !visitId) {
        throw new Error("invalid params");
    }

    return (
        <MapPanel flat>
            {visit.hasStagedChanges && <Alert severity="warning">{t("projectReviewVisitV2.overview.hasStagedChanges")}</Alert>}
            <ProjectReviewVisitV2StatusHandler visit={visit} />

            {(visit.status === GqlPlannerVisitStatus.AwaitingApproval ||
                visit.status === GqlPlannerVisitStatus.AwaitingReport ||
                visit.status === GqlPlannerVisitStatus.AwaitingObservationApproval ||
                visit.status === GqlPlannerVisitStatus.AwaitingConfirm ||
                visit.status === GqlPlannerVisitStatus.Approved) && (
                <ProjectReviewVisitV2OverviewNavigation panelStatus={panelStatus} />
            )}

            {(visit.status === GqlPlannerVisitStatus.AwaitingApproval ||
                visit.status === GqlPlannerVisitStatus.AwaitingObservationApproval ||
                visit.status === GqlPlannerVisitStatus.AwaitingConfirm) && (
                <ProjectReviewVisitV2ApprovalButton
                    onApprove={onApprove}
                    loading={loading}
                    isEverythingReady={isEverythingReady}
                />
            )}

            {visit.status === GqlPlannerVisitStatus.Approved && (
                <ProjectReviewVisitV2ApprovalDisplay
                    visit={visit}
                    finalReportHref={generatePath("/c/:clientSlug/p/:projectSlug/review-visit/:visitId/finalReport", {
                        clientSlug,
                        projectSlug,
                        visitId,
                    })}
                    showFinalReportButton={showFinalReportButton}
                />
            )}
        </MapPanel>
    );
}
